/**
 * App Routes — React Router v7
 *
 * The scheduling/clinical surfaces currently use state-based nav in
 * App.tsx; the admin module adds router-based routing under /admin.
 * AppRouter provides a BrowserRouter + the minimal route tree the admin
 * app needs. Non-admin paths render the legacy state-nav App (which
 * handles /billing/* and /settings/* internally so the SideNav stays
 * visible).
 *
 * PROD-861: `<AppIntegrations />` + `<UpgradeAlert />` mount INSIDE the
 * BrowserRouter so the Mixpanel route observer has access to
 * `useLocation`. They render nothing (UpgradeAlert renders only when
 * the version check returns stale).
 */
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AdminRoutes } from '@/admin';
import App from '@/App';
import { AppIntegrations } from '@/app/integrations';
import { UpgradeAlert } from '@/app/layout/upgrade-alert';
import { ActiveSessionConflictDialog } from '@/auth/active-session-conflict-dialog';
import { TimezoneMismatchDialog } from '@/auth/timezone-mismatch-dialog';
import { SessionExpiredDialog } from '@/auth/session-expired-dialog';
import { PmLoginPage } from '@/auth/pm-login-page';
import { ForgotPasswordPage } from '@/auth/forgot-password-page';
import { ResetPasswordAccessCodePage } from '@/auth/reset-password-access-code-page';
import { ResetPasswordNewPasswordPage } from '@/auth/reset-password-new-password-page';
import { CreatePasswordPage } from '@/auth/create-password-page';
import { SignupWithAccessCodePage } from '@/auth/signup-with-access-code-page';
import { PasswordChangedPage } from '@/auth/password-changed-page';

export function AppRouter() {
  return (
    <BrowserRouter>
      <AppIntegrations />
      <UpgradeAlert />
      {/* PROD-946 — "Ongoing Session" confirmation modal. Mounts here
          (not under App) so it surfaces on /login too (login-path 409
          from GetUserDataToken), not just inside the authenticated
          shell. Reads `useAuthStore.activeSessionConflict` directly. */}
      <ActiveSessionConflictDialog />
      {/* PROD-1026 (F-AUT-046): timezone-mismatch recovery popup — also
          mounted at app root so it surfaces during the login flow. Reads
          `useAuthStore.timezoneMismatch` directly. */}
      <TimezoneMismatchDialog />
      {/* F-AUT-089: session-expired modal — shown when a token refresh
          fails on an active session (auth-store parks instead of silent
          logout). OK → logout + redirect. Reads
          `useAuthStore.sessionExpired` directly. */}
      <SessionExpiredDialog />
      <Routes>
        <Route path="/admin/*" element={<AdminRoutes />} />
        {/* PROD-885 Phase 1 — PM login page. PROD-926 stripped the
            DEV_CONFIG bootstrap so the App's unauthenticated-redirect
            guard now fires in dev too — an empty/cleared session lands
            here in every env. */}
        <Route path="/login" element={<PmLoginPage />} />
        {/* PROD-889 Phase 2 — Forgot Password. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/* PROD-890 Phase 3a + 3b — Reset Password access-code entry +
            new-password entry. Both require prior-step `location.state`;
            direct URL hits redirect back to /forgot-password. */}
        <Route
          path="/reset-password-access-code"
          element={<ResetPasswordAccessCodePage />}
        />
        <Route
          path="/reset-password-new-password"
          element={<ResetPasswordNewPasswordPage />}
        />
        {/* PROD-891 Phase 4 — Create Password via email link. Reads
            `?email=` + `?token=` from URL; redirects to /login if absent. */}
        <Route path="/create-password" element={<CreatePasswordPage />} />
        {/* PROD-892 Phase 5 — Signup via Access Code. On success →
            /create-password?email=&token= (Phase 4 route). */}
        <Route path="/signup" element={<SignupWithAccessCodePage />} />
        {/* PROD-893 Phase 6 — terminal success page reached from Phase 3b,
            Phase 4, and Phase 5 → 4 chains. */}
        <Route path="/password-changed" element={<PasswordChangedPage />} />
        <Route path="*" element={<App />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppRouter;
